import { ApiElements } from './api';

export enum Status {
  PAID = 'PAID',
  UNPAID = 'UNPAID',
  EXPIRED = 'EXPIRED',
  MAX_PAY_ATTEMPTS = 'MAX_PAY_ATTEMPTS',
  CANCELLED = 'CANCELLED'
}

export enum ACH_ERROR_MSG {
  Error = 'error',
  Ineligible = 'ineligible',
  RiskDecline = 'riskOrNegativeDecline',
  NegativeDecline = 'riskOrNegativeDecline',
}

export type Logo = {
  logoFilename: string;
  logoType: string;
  url: string;
};

export const SettingNames = {
  ECOMM_CHECKOUT_CUSTOM_BUSINESS_LOGO: 'BUSINESS_LOGO',
  ECOMM_CHECKOUT_CUSTOM_BUSINESS_NAME: 'BUSINESS_NAME',
  ECOMM_CHECKOUT_CUSTOM_HEADER_COLOR: 'HEADER_COLOR',
  ECOMM_CHECKOUT_CUSTOM_FOOTER_COLOR: 'FOOTER_COLOR',
  ECOMM_CHECKOUT_CUSTOM_BACKGROUND_COLOR: 'BACKGROUND_COLOR',
  ECOMM_CHECKOUT_CUSTOM_FOOTER_TEXT: 'FOOTER_TEXT',
  ECOMM_CHECKOUT_FAIL_URL: 'FAIL_URL',
  ECOMM_CHECKOUT_SUCCESS_URL: 'SUCCESS_URL',
  ECOMM_CHECKOUT_CANCEL_URL: 'CANCEL_URL',
  ECOMM_CHECKOUT_RECAPTCHA_ENABLED: 'ECOMM_CHECKOUT_RECAPTCHA_ENABLED',
  ECOMM_CHECKOUT_CUSTOM_BUSINESS_LOGO_SIZE: 'LOGO_SIZE',
  ECOMM_CHECKOUT_ORDER_NOTE_ENABLED: 'ORDER_NOTE_ENABLED',
  HCO_NOTE_SETTING_ENABLED : 'HCO_NOTE_SETTING_ENABLED',
  HCO_CUSTOMER_INFO_FEATURE_ENABLED : 'HCO_CUSTOMER_INFO_FEATURE_ENABLED',
  HCO_CUSTOMER_INFO_SETTINGS_ENABLED: 'HCO_CUSTOMER_INFO_SETTINGS_ENABLED'
} as const;

export type SettingName = keyof typeof SettingNames;

export type SettingValue = string;

export type MerchantSetting = {
  name: SettingName;
  value: SettingValue;
};

export type Merchant = {
  id: Uuid;
  name: string;
  logos?: ApiElements<Logo>;
  properties: {
    defaultCurrency: string;
    locale: string;
  };
  merchantSettings?: ApiElements<MerchantSetting>;
};

export type Customer = {
  firstName?: string;
  lastName?: string;
  phoneNumber?: ApiElements<{ phoneNumber: string }>;
  phoneNumbers?: ApiElements<{ phoneNumber: string }>;
  email: ApiElements<{ emailAddress: string }>;
  emailAddresses?: ApiElements<{ emailAddress: string }>;
  addresses?: ApiElements<AddressFields>;
  address?: ApiElements<AddressFields>;
  metadata?: customerMetaDataFields;
};

export type CustomerRequest = {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  email: string;
  address?: AddressFields | {};
  customerMetaData?: customerMetaDataFields | {};
};

export type customerMetaDataFields = {
  businessName: string;
}

export type AddressFields = {
  address1: string;
  address2: string;
  state: string;
  city: string;
  zip: string;
  country?: string;
}

export type Invoice = {
  id: Uuid;
  createdTime: number;
  dueDate: number;
  title: string;
  notes: string;
};

export type LineItem = {
  name: string;
  price: number;
  unitQty: number;
};

export type TaxSummary = {
  id: Uuid;
  name: string;
  amount: number;
};

export type ShoppingCart = {
  lineItems?: LineItem[];
  total: number;
  subtotal?: number;
  totalTaxAmount?: number;
  taxSummary?: TaxSummary[];
  tipAmount?: number;
  minAmount?: number;
};

export type RedirectUrls = {
  success?: string;
  failure?: string;
};

export type CardType =
  | 'AMEX'
  | 'CARTE_BLANCHE'
  | 'CHINA_UNION_PAY'
  | 'DINERS_CLUB'
  | 'DISCOVER'
  | 'EBT'
  | 'GIFT_CARD'
  | 'GIROCARD'
  | 'INTERAC'
  | 'JCB'
  | 'LASER'
  | 'MAESTRO'
  | 'MC'
  | 'OTHER'
  | 'SOLO'
  | 'UNKNOWN'
  | 'VISA';

export type SourceType = 'G_PAY' | 'APPLE_PAY' | 'ACH' | 'CARD' | 'GIFT_CARD';

export type Source = {
  source: string;
  sourceType: SourceType;
  amount: number,
};

export enum CheckoutType {
  CHECKOUT = 'checkout',
  INVOICE = 'invoice',
  PAY_WIDGETS = 'pay-widgets',
}

export type AdditionalChargeType = 'CREDIT_SURCHARGE' | 'CONVENIENCE_FEE';

export type AdditionalCharge = {
  id?: Uuid;
  type: AdditionalChargeType;
  rate?: number;
  amount: number;
}

export type CheckoutSession = {
  id: Uuid;
  paymentDetails?: CheckoutPayResponse;
  status: Status;
  merchant: Merchant;
  customer?: Customer;
  invoice?: Invoice;
  shoppingCart: ShoppingCart;
  redirectUrls?: RedirectUrls;
  createdTime: number;
  type?: CheckoutType;
  widgetType?: string;
  additionalCharges?: AdditionalCharge[];
};

export type CheckoutPayResponse = {
  id: Uuid;
  amount: number;
  lastFour: string;
  cardType?: CardType;
  status: Status;
  additionalCharges?: AdditionalCharge[];
  orderNote?: string;
  paymentMethodDetails?: string;
};

export enum PaymentLinkType {
  BUTTON = 'button',
  LINK = 'link',
  QR = 'qr'
}

export type PayWidget = {
  uuid: Uuid;
  amount: number;
  minAmount: number;
  name: string;
  merchant: Merchant;
  widgetDetail: {
    type: PaymentLinkType;
  };
  createdTime: string;
  convenienceFee?: number;
}

export type WalletResponse = {
  token: string;
  customer: {
    email: string;
    billingInfo: {
      cardNetwork: string;
      cardDetails: string;
      billingAddress: {
        countryCode: string;
        postalCode: string;
        name: string;
      };
    };
  };
}

export type GiftCard = {
  token: string;
  last4: string;
  balance: number
};

export type SclBalanceInquiryResponse = {
  status: string;
  gift_card: {
    expiration_date: string;
    previous_balance: number;
    new_balance: number;
    lock_amount: number;
  }
}

export type BalanceInquiryResponse = {
  status: string;
  giftCard: {
    expirationDate: string;
    balance: number;
  }
}


export type BalanceInquiryRequest = {
  source: string;
}

